<template>
    <div class="ActionsList">
        <div v-if="showEntityScopeNotice" class="ActionsList__scopeNotice">
            <EidIcon class="ActionsList__scopeNoticeIcon" :icon-src="faCircleInfo" />
            <span>
                Entity-scoped actions only. Global actions are no longer attributed to a single entity
                once they are resolved, so a global action resolved for this entity appears in the
                RP-wide Actions Queue instead.
                <NewButton
                    class="ActionsList__scopeNoticeLink"
                    variant="link"
                    @click="goToRpWideQueue"
                >
                    Open the Actions Queue
                </NewButton>
            </span>
        </div>
        <EntityActionsTable
            :tab="tab"
            :entity-actions="listStatus.list"
            :loading="listStatus.loading"
            :finished="listStatus.finished"
            :total="listStatus.total"
            :no-action-message="effectiveNoActionMessage"
            :requirement-definitions="requirementDefinitions"
            :search-term="filters.search || ''"
            :sorting="sorting"
            @review="$emit('review', $event)"
            @scroll-end="loadNext"
        >
            <template #toolbar>
                <ActionsSearchAndFilter
                    class="ActionsList__toolbar"
                    :filters="filters"
                    :tab="tab"
                    :entity-name="entityName"
                    @change="$emit('filters-change', $event)"
                    @remove-filter="$emit('remove-filter', $event)"
                />
            </template>
            <template #toolbarEnd>
                <div class="ActionsList__sortContainer">
                    <span class="ActionsList__sortLabel">Sort by:</span>
                    <Dropdown
                        class="ActionsList__sortDropdown"
                        :options="sortOptions"
                        :selected="selectedSortOption"
                        placeholder="Sort"
                        @input="onSortChange"
                    />
                </div>
            </template>
        </EntityActionsTable>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import noop from 'lodash/noop';

    import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';

    import Dropdown from '@evidentid/dashboard-commons/components/Dropdown/Dropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { ActionsTab } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { serializeSorting, Sorting, SortingDirection } from '@evidentid/universal-framework/sorting';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import ActionsSearchAndFilter, {
        hasResolutionFilter,
        RESOLUTION_FILTER_EMPTY_MESSAGE,
    } from '@/modules/entity-actions-review/components/ActionsSearchAndFilter/ActionsSearchAndFilter.vue';
    import EntityActionsTable
        from '@/modules/entity-actions-review/components/EntityActionsTable/EntityActionsTable.vue';
    import { ACTIONS_SORT_COLUMNS, ACTIONS_TAB_QUERY_KEY } from '@/modules/entity-actions-review/constants';
    import { EntityActionsListStatus, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';
    import { ActionsListFilters } from '@/modules/entity-actions-review/types';

    interface SortOption extends DropdownOption<string> {
        column: string;
        direction: SortingDirection;
    }

    const sortOptions: SortOption[] = [
        { label: 'Oldest First', value: 'asc', column: ACTIONS_SORT_COLUMNS.createdAt, direction: SortingDirection.asc },
        { label: 'Newest First', value: 'desc', column: ACTIONS_SORT_COLUMNS.createdAt, direction: SortingDirection.desc },
    ];

    // Shown whenever some filter other than entityId narrowed the result to nothing.
    // entityId's own emptiness is explained by the host's noActionMessage instead, since it
    // has the entity's name and the tab's wording to work with.
    export const NO_MATCHING_ACTIONS_MESSAGE = 'No actions match these filters.';

    // The terminal fallback when a tab is empty with no filter active at all.
    const tabDefaultMessage: Record<ActionsTab, string> = {
        [ActionsTab.pending]: 'All available actions have been completed.',
        [ActionsTab.completed]: 'No actions have been completed yet.',
    };

    function hasNonEntityFilter(filters: ActionsListFilters): boolean {
        return Object.entries(filters).some(([ key, value ]) => key !== 'entityId'
            && (Array.isArray(value) ? value.length > 0 : Boolean(value)));
    }

    const emptyListStatus: EntityActionsListStatus = {
        status: OperationStatus.uninitialized,
        list: [],
        loading: false,
        finished: false,
        total: null,
        loadKey: null,
    };

    export default defineComponent({
        name: 'ActionsList',
        components: { ActionsSearchAndFilter, Dropdown, EidIcon, EntityActionsTable, NewButton },
        props: {
            tab: {
                type: String as PropType<ActionsTab>,
                required: true,
            },
            rpName: {
                type: String as PropType<string>,
                default: '',
            },
            filters: {
                type: Object as PropType<ActionsListFilters>,
                default: (): ActionsListFilters => ({}),
            },
            sorting: {
                type: Object as PropType<Sorting>,
                required: true,
            },
            requirementDefinitions: {
                type: Object as PropType<Record<string, TprmRequirementDefinition>>,
                default: (): Record<string, TprmRequirementDefinition> => ({}),
            },
            entityName: {
                type: String as PropType<string>,
                default: 'Entity',
            },
            noActionMessage: {
                type: String as PropType<string | null>,
                default: null,
            },
            // Bumped by the host when something outside this list invalidates it, such as
            // contract extractions finishing.
            reloadToken: {
                type: Number as PropType<number>,
                default: 0,
            },
        },
        emits: [ 'review', 'sort-change', 'remove-filter', 'filters-change' ],
        data() {
            return { sortOptions, faCircleInfo };
        },
        computed: {
            listStatus(): EntityActionsListStatus {
                return useEntityActionsReviewStore().entityActions[this.rpName]?.[this.tab] || emptyListStatus;
            },
            selectedSortOption(): SortOption[] {
                const selected = this.sortOptions.find((option) => option.direction === this.sorting?.direction);
                return selected ? [ selected ] : [ this.sortOptions[0] ];
            },
            loadPayload(): {
                rpName: string; tab: ActionsTab; sort: string | null; filters?: ActionsListFilters; loadKey: string;
            } {
                return {
                    rpName: this.rpName,
                    tab: this.tab,
                    sort: serializeSorting(this.sorting) || null,
                    loadKey: this.loadKey,
                    ...(Object.keys(this.filters).length > 0 && { filters: this.filters }),
                };
            },
            // One string covering everything the request depends on, so a change to any of
            // them triggers exactly one reload. Also carried on every load/loadNext call, so
            // the store can tell a response for the tab's current load apart from one left
            // over from a load this key has since superseded.
            loadKey(): string {
                return JSON.stringify([ this.rpName, this.tab, this.sorting, this.filters, this.reloadToken ]);
            },
            // Answers "did a filter cause this empty list?" before "which filter?": the most
            // specific reason wins.
            //   1. A resolution facet (outcome/resolvedFrom/resolvedTo/resolvedBy) excludes
            //      the whole pre-feature backlog by construction -- most of the Completed tab
            //      at launch -- so it always names the cause when it is set.
            //   2. Any other active filter besides entityId (search, action type, a date
            //      range, scope) narrowed the result itself, so a generic "no match" message
            //      is more honest than either the host's entity-scoped wording or this tab's
            //      default -- both of which claim there is nothing left to do, not that a
            //      filter didn't match.
            //   3. With only entityId set (or nothing at all), the host's noActionMessage
            //      speaks to that entity; failing that, this tab's own default applies.
            effectiveNoActionMessage(): string | null {
                if (hasResolutionFilter(this.filters)) {
                    return RESOLUTION_FILTER_EMPTY_MESSAGE;
                }
                if (hasNonEntityFilter(this.filters)) {
                    return NO_MATCHING_ACTIONS_MESSAGE;
                }
                return this.noActionMessage || tabDefaultMessage[this.tab];
            },
            // Resolving an action clears its per-entity evaluation link, leaving only the
            // action's own entity id. A global action has none, so once resolved it can only
            // ever surface on the RP-wide Completed tab, never on a single entity's -- even
            // though it showed up in that entity's Pending list beforehand.
            showEntityScopeNotice(): boolean {
                return this.tab === ActionsTab.completed && Boolean(this.filters.entityId);
            },
        },
        watch: {
            // immediate makes this fire both on a genuine query change and on every
            // remount -- this component unmounts whenever the detail view opens, so
            // returning from it re-runs this handler with the same rpName/tab/sorting/
            // filters/reloadToken it already had. Only a real query change should clear
            // the cached list and start over; a remount with the same loadKey already has
            // the store's list for it (the store keys its cache by this same string), so
            // reloading would throw away a patched row and any scrolled-in pages for no
            // reason. A failed load is not treated as fresh, so returning to a tab that
            // errored still retries instead of leaving the failure in place.
            loadKey: {
                immediate: true,
                async handler(): Promise<void> {
                    if (!this.rpName) {
                        return;
                    }
                    if (this.listStatus.loadKey === this.loadKey && this.listStatus.status !== OperationStatus.error) {
                        return;
                    }
                    useEntityActionsReviewStore().clearEntityActionsTab({ rpName: this.rpName, tab: this.tab });
                    await useEntityActionsReviewStore().loadEntityActions(this.loadPayload);
                },
            },
        },
        methods: {
            async loadNext(): Promise<void> {
                if (!this.listStatus.loading && !this.listStatus.finished) {
                    await useEntityActionsReviewStore().loadNextEntityActions(this.loadPayload);
                }
            },
            onSortChange(option: SortOption): void {
                this.$emit('sort-change', { column: option.column, direction: option.direction });
            },
            goToRpWideQueue(): void {
                this.$router.push({
                    name: 'entityActionsReview',
                    query: { [ACTIONS_TAB_QUERY_KEY]: ActionsTab.completed },
                } as any).catch(noop);
            },
        },
    });
</script>
